/**
 * Pure display helpers for the compete surfaces. These are LOOKUPS and
 * FORMATTERS over server-provided values — never rating math, never a derived
 * win rate, never a fabricated number. The one allowed "computation" is the
 * tier → label/slug map (a fixed external scale, not a calculation on the
 * player's data).
 */

const EM_DASH = "—";

/** The privacy-safe placeholder for a missing display_name (never the uid). */
export const MISSING = EM_DASH;

/** Format a server-owned rating or points value: always three decimals. */
export function ratingLabel(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value);
}

/** Title-case a snake/space token: "first_win" → "First Win". */
export function humanize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Lower-cased tier slug for the CSS chip class (e.g. "Gold" → "tier--gold"). */
export function tierSlug(tier: string): string {
  return tier
    .trim()
    .toLowerCase()
    .replace(/[^a-z]+/g, "-");
}

/** Display label for a tier — the server string, title-cased. */
export function tierLabel(tier: string): string {
  return humanize(tier);
}

const ROMAN = ["", "I", "II", "III", "IV", "V"];

/**
 * Roman-numeral label for a division (a SERVER value, 1-5). Returns null when
 * there is no division (e.g. Grandmaster), so the badge renders no division at
 * all rather than a fabricated "I".
 */
export function divisionLabel(
  division: number | null | undefined,
): string | null {
  if (division == null) return null;
  return ROMAN[division] ?? String(division);
}

/**
 * Format a server win-rate fraction (0..1) as a percentage STRING. Returns the
 * em-dash when there are zero games — a win rate is meaningless with no games,
 * so we never present a misleading "0%". The percentage itself is a server
 * value formatted, not computed from W/L on the client.
 */
export function winRateLabel(winRate: number, gamesPlayed: number): string {
  if (gamesPlayed <= 0) return EM_DASH;
  const pct = winRate * 100;
  // Trim a trailing ".0" so 50% reads "50%", but keep 62.5%.
  const rounded = Math.round(pct * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
}

export function matchSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    queue: "Ranked drop-in", league: "League", tournament: "Tournament",
  };
  return labels[source] ?? "Recorded match";
}

/**
 * Format a match's server `created_at` timestamp as a short calendar date
 * ("Jun 1, 2026"). FORMATS a server value — it never computes a relative "3 days
 * ago" or fabricates a date. Returns null for a missing value (the row renders no
 * date chip rather than a fabricated one); an unparseable value falls back to the
 * raw string only as a last resort. The year is always shown so the formatted
 * output is unambiguously NOT the raw ISO blob.
 */
export function matchDateLabel(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value;
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(ms));
  } catch {
    return value;
  }
}

/** What a player shows instead of a tier until placement is complete. */
export const UNRATED = "Unrated";

/**
 * "Silver Division 4" for a placed player; "Unrated" before placement,
 * because the tier then is only the provisional default, not a real level.
 */
export function standingLabel(standing: {
  tier: string;
  division?: number | null;
  is_placed: boolean;
}): string {
  if (!standing.is_placed) return UNRATED;
  return `${tierLabel(standing.tier)}${standing.division == null ? "" : ` Division ${standing.division}`}`;
}
